import React from "react";
import { loadPyodide, PyodideInterface } from "pyodide";
import Editor, { OnMount } from "@monaco-editor/react";
import {
  Play,
  Trash2,
  Plus,
  Download,
  Terminal,
  CloudUpload,
  FlaskConical,
} from "lucide-react";

// If you create a backend for cloud save, put its URL here:
const CLOUD_SAVE_ENDPOINT = "https://your-api.example.com/algomind/save";

// Simple folder helper: everything before first "/" is folder name.
function getFolderName(path: string) {
  const idx = path.indexOf("/");
  return idx === -1 ? "root" : path.slice(0, idx);
}

// A few static completions to feel “AI assisted”
const PY_KEYWORDS = [
  "def",
  "class",
  "import",
  "from",
  "return",
  "yield",
  "async",
  "await",
  "for",
  "while",
  "if",
  "elif",
  "else",
  "try",
  "except",
  "finally",
  "with",
  "as",
  "lambda",
];

const PY_COMMON_IMPORTS = [
  "import numpy as np",
  "import pandas as pd",
  "import matplotlib.pyplot as plt",
  "import random",
  "import math",
  "import statistics",
];

const PY_SNIPPETS = [
  {
    label: "for-range",
    insertText: "for i in range(10):\n    print(i)",
    detail: "For loop over range",
  },
  {
    label: "if-main",
    insertText: 'if __name__ == "__main__":\n    main()',
    detail: "Main guard",
  },
];

const INITIAL_FILES: Record<string, string> = {
  "main.py": `"""
AlgoMind Academy · Python Playground

Use this file to try ideas, mini-algorithms, or data science experiments.
"""

print("Hello from AlgoMind Academy!")`,
  "tests/test_math_utils.py": `import unittest

def add(a, b):
    return a + b

class TestMathUtils(unittest.TestCase):
    def test_add(self):
        self.assertEqual(add(2, 3), 5)

if __name__ == "__main__":
    unittest.main()
`,
};

// Read-only templates for assignments
const TEMPLATE_FILES: Record<string, string> = {
  "templates/assignment1.py": `# 🚫 READ ONLY — Assignment Template
# Create a new file like solutions/assignment1_solution.py
# and write your solution there.

def solve():
    return "template"
`,
  "templates/data/sample.txt": `This is sample data (read-only).

You can open & read this file from your Python code, 
but you cannot modify it from the editor UI.`,
};

type CloudState = "idle" | "saving" | "ok" | "error";

export default function AlgoMindPlayground() {
  const pyodideRef = React.useRef<PyodideInterface | null>(null);
  const [pyStatus, setPyStatus] = React.useState<
    "idle" | "loading" | "ready" | "error"
  >("idle");

  const [files, setFiles] = React.useState<Record<string, string>>(() => {
    const saved = localStorage.getItem("algomind_playground_v2");
    let studentFiles: Record<string, string> = INITIAL_FILES;
    if (saved) {
      try {
        studentFiles = JSON.parse(saved);
      } catch {
        studentFiles = INITIAL_FILES;
      }
    }
    // Templates are always present and read-only.
    return { ...TEMPLATE_FILES, ...studentFiles };
  });

  const [activeFile, setActiveFile] = React.useState<string>("main.py");
  const [output, setOutput] = React.useState<string>("");
  const [plots, setPlots] = React.useState<string[]>([]);
  const [replInput, setReplInput] = React.useState("");
  const [replHistory, setReplHistory] = React.useState<string[]>([]);
  const [cloudState, setCloudState] = React.useState<CloudState>("idle");
  const [cloudMsg, setCloudMsg] = React.useState<string | null>(null);

  // Autosave only non-template files
  React.useEffect(() => {
    const toSave: Record<string, string> = {};
    for (const [path, content] of Object.entries(files)) {
      if (!path.startsWith("templates/")) {
        toSave[path] = content;
      }
    }
    localStorage.setItem("algomind_playground_v2", JSON.stringify(toSave));
  }, [files]);

  // ---- PYODIDE SETUP --------------------------------------------------------
  const ensurePyodide = React.useCallback(async () => {
    if (pyodideRef.current) return pyodideRef.current;

    try {
      setPyStatus("loading");

      // MUST match your installed pyodide version: 0.29.0
      const py = await loadPyodide({
        indexURL: "https://cdn.jsdelivr.net/pyodide/v0.29.0/full/",
      });

      // Load matplotlib + micropip for later pip installs
      // @ts-ignore
      await py.loadPackage(["micropip", "matplotlib"]);

      // Matplotlib plot-capture helpers
      await py.runPythonAsync(`
import sys, io, builtins

import matplotlib
matplotlib.use("Agg")
import matplotlib.pyplot as plt
import base64

_am_plot_images = []

def _am_reset_plots():
    global _am_plot_images
    _am_plot_images = []

def _am_capture_plot():
    import io
    buf = io.BytesIO()
    plt.savefig(buf, format="png")
    buf.seek(0)
    _am_plot_images.append(base64.b64encode(buf.read()).decode("ascii"))
    plt.close()

plt.show = _am_capture_plot
`);

      pyodideRef.current = py;
      setPyStatus("ready");
      return py;
    } catch (err) {
      console.error("Pyodide load error", err);
      setPyStatus("error");
      throw err;
    }
  }, []);

  // ---- Write all files into Pyodide FS --------------------------------------
  async function syncFilesToPyodide() {
    const py = await ensurePyodide();

    // Recreate folders + write files
    for (const [path, content] of Object.entries(files)) {
      const folderParts = path.split("/").slice(0, -1);
      let current = "";
      for (const part of folderParts) {
        if (!part) continue;
        current = current ? `${current}/${part}` : part;
        try {
          py.FS.mkdir(current);
        } catch {
          /* folder may already exist */
        }
      }
      py.FS.writeFile(path, content);
    }
    py.FS.chdir("/");
    return py;
  }

  // ---- RUN MAIN FILE --------------------------------------------------------
  async function runCode() {
    setOutput("⏳ Running...\n");
    setPlots([]);
    try {
      const py = await syncFilesToPyodide();

      let captured = "";
      py.setStdout({ batched: (s: string) => (captured += s) });
      py.setStderr({ batched: (s: string) => (captured += s) });

      await py.runPythonAsync("_am_reset_plots()");
      await py.runPythonAsync(
        `import runpy; runpy.run_path("${activeFile}", run_name="__main__")`
      );

      const imgs = py.globals.get("_am_plot_images");
      const arr = (imgs as any).toJs ? (imgs as any).toJs() : imgs;
      setPlots(Array.from(arr as any));

      setOutput(captured || "✅ Finished (no stdout printed).");
    } catch (err: any) {
      setOutput(`❌ Error:\n${String(err)}`);
    }
  }

  // ---- RUN UNIT TESTS -------------------------------------------------------
  async function runTests() {
    setOutput("⏳ Running tests...\n");
    setPlots([]);
    try {
      const py = await syncFilesToPyodide();

      let captured = "";
      py.setStdout({ batched: (s: string) => (captured += s) });
      py.setStderr({ batched: (s: string) => (captured += s) });

      await py.runPythonAsync(`
import unittest, io, sys, os

loader = unittest.defaultTestLoader
suite = loader.discover(".", pattern="test_*.py")

buf = io.StringIO()
runner = unittest.TextTestRunner(stream=buf, verbosity=2)

result = runner.run(suite)
buf.getvalue()
`);

      const testOutput = py.runPython("_");
      setOutput(String(testOutput || "No tests found (pattern test_*.py)."));
    } catch (err: any) {
      setOutput(`❌ Test run error:\n${String(err)}`);
    }
  }

  // ---- TERMINAL/REPL WITH pip install ---------------------------------------
  async function runRepl() {
    const code = replInput.trim();
    if (!code) return;

    const py = await ensurePyodide();

    // pip install support: e.g. "pip install numpy"
    if (code.startsWith("pip install")) {
      const pkg = code.replace("pip install", "").trim();
      if (!pkg) return;

      setReplHistory((h) => [...h, `>>> ${code}`]);

      try {
        await py.runPythonAsync(`
import micropip
await micropip.install("${pkg}")
`);
        setReplHistory((h) => [...h, `✔ Installed ${pkg}\n`]);
      } catch (err: any) {
        setReplHistory((h) => [
          ...h,
          `❌ Install failed for ${pkg}: ${String(err)}`,
        ]);
      }

      setReplInput("");
      return;
    }

    // normal REPL execution
    try {
      let captured = "";
      py.setStdout({ batched: (s: string) => (captured += s) });
      py.setStderr({ batched: (s: string) => (captured += s) });

      await py.runPythonAsync(code);

      const line = `>>> ${code}\n${captured || ""}`;
      setReplHistory((prev) => [...prev, line]);
      setReplInput("");
    } catch (err: any) {
      const line = `>>> ${code}\n❌ ${String(err)}`;
      setReplHistory((prev) => [...prev, line]);
      setReplInput("");
    }
  }

  // ---- FILE HANDLING --------------------------------------------------------
  const createFile = () => {
    let name = prompt(
      "New file path (e.g. scripts/demo.py or utils.py):",
      "script.py"
    );
    if (!name) return;
    if (!name.endsWith(".py")) name += ".py";
    if (files[name]) {
      alert("File already exists.");
      return;
    }
    setFiles({ ...files, [name]: "# New Python file\n" });
    setActiveFile(name);
  };

  const deleteFile = () => {
    if (activeFile.startsWith("templates/")) {
      alert("❌ This is a read-only template file and cannot be deleted.");
      return;
    }

    const nonTemplateCount = Object.keys(files).filter(
      (f) => !f.startsWith("templates/")
    ).length;

    if (nonTemplateCount <= 1) {
      alert("At least one non-template file must remain.");
      return;
    }

    if (!window.confirm(`Delete ${activeFile}?`)) return;

    const newFiles = { ...files };
    delete newFiles[activeFile];
    const next =
      Object.keys(newFiles).find((f) => !f.startsWith("templates/")) ||
      Object.keys(newFiles)[0];
    setFiles(newFiles);
    setActiveFile(next);
  };

  const downloadFile = () => {
    const blob = new Blob([files[activeFile] || ""], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = activeFile;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- CLOUD SAVE (per intern with userId) ----------------------------------
  async function saveToCloud() {
    if (!CLOUD_SAVE_ENDPOINT) {
      alert(
        "CLOUD_SAVE_ENDPOINT is not configured. Set it in AlgoMindPlayground.tsx."
      );
      return;
    }

    const userId =
      localStorage.getItem("intern_user_id") || "guest"; // set this when user logs in

    setCloudState("saving");
    setCloudMsg(null);

    try {
      const res = await fetch(CLOUD_SAVE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          files,
          savedAt: new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCloudState("ok");
      setCloudMsg("Saved to cloud successfully.");
      setTimeout(() => setCloudState("idle"), 2500);
    } catch (err: any) {
      console.error(err);
      setCloudState("error");
      setCloudMsg("Cloud save failed. Check endpoint / network.");
      setTimeout(() => setCloudState("idle"), 3000);
    }
  }

  // ---- MONACO AI-ish SUGGESTIONS -------------------------------------------
  const handleEditorMount: OnMount = (editor, monaco) => {
    monaco.languages.registerCompletionItemProvider("python", {
      provideCompletionItems: () => {
        const suggestions: any[] = [];

        suggestions.push(
          ...PY_KEYWORDS.map((kw) => ({
            label: kw,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: kw,
          }))
        );

        suggestions.push(
          ...PY_COMMON_IMPORTS.map((imp) => ({
            label: imp,
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: imp,
            detail: "Common import",
          }))
        );

        suggestions.push(
          ...PY_SNIPPETS.map((snip) => ({
            label: snip.label,
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: snip.insertText,
            detail: snip.detail,
          }))
        );

        return { suggestions };
      },
    });
  };

  // ---- RENDER ---------------------------------------------------------------
  const folders = Array.from(
    new Set(Object.keys(files).map((path) => getFolderName(path)))
  );

  const isTemplate = activeFile.startsWith("templates/");

  return (
    <div className="min-h-screen w-full bg-[#020617] text-white flex flex-col">
      {/* Soft background gradient */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
      >
        <div className="absolute -top-40 -left-24 h-80 w-80 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="absolute -bottom-40 -right-24 h-80 w-80 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_#1d3557_0,_#020617_52%)] opacity-90" />
      </div>

      {/* HEADER */}
      <header className="border-b border-white/10 px-5 sm:px-8 py-3 flex items-center justify-between bg-black/30 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-xl overflow-hidden">
  <img
    src="algo.png"   // ← your logo path
    alt="Logo"
    className="h-full w-full object-cover"
  />
</div>

          <div>
            <div className="text-sm sm:text-base font-semibold tracking-tight">
              AlgoMind Academy{" "}
              <span className="text-sky-300">Python Playground</span>
            </div>
            <div className="text-[11px] text-white/60">
              Browser-based Python lab for interns • Code • Tests • Plots • REPL
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 text-[11px] sm:text-xs">
          <span
            className={`hidden sm:inline-flex items-center gap-1 rounded-full border px-2 py-1 ${
              pyStatus === "ready"
                ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-200"
                : pyStatus === "loading"
                ? "border-amber-400/40 bg-amber-400/10 text-amber-100"
                : pyStatus === "error"
                ? "border-red-400/40 bg-red-400/10 text-red-100"
                : "border-slate-400/40 bg-slate-400/10 text-slate-100"
            }`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {pyStatus === "ready"
              ? "Runtime ready"
              : pyStatus === "loading"
              ? "Loading runtime…"
              : pyStatus === "error"
              ? "Runtime error"
              : "Runtime idle"}
          </span>

          <button
            onClick={runTests}
            className="hidden sm:inline-flex items-center gap-1.5 bg-emerald-500/90 hover:bg-emerald-500 px-3 py-1.5 rounded-full font-medium"
          >
            <FlaskConical size={14} /> Tests
          </button>

          <button
            onClick={saveToCloud}
            className="hidden sm:inline-flex items-center gap-1.5 bg-slate-800/90 hover:bg-slate-700 px-3 py-1.5 rounded-full font-medium"
          >
            <CloudUpload size={14} />
            {cloudState === "saving" ? "Saving…" : "Cloud save"}
          </button>

          <button
            onClick={runCode}
            className="inline-flex items-center gap-1.5 bg-sky-500 hover:bg-sky-600 px-4 py-1.5 rounded-full font-semibold text-xs sm:text-sm shadow-sm shadow-sky-500/40"
          >
            <Play size={14} /> Run
          </button>
        </div>
      </header>

      {/* CLOUD STATUS BANNER */}
      {cloudMsg && (
        <div
          className={`text-xs px-5 sm:px-8 py-2 ${
            cloudState === "error"
              ? "bg-red-500/15 text-red-100"
              : "bg-emerald-500/10 text-emerald-100"
          }`}
        >
          {cloudMsg}
        </div>
      )}

      {/* BODY */}
      <div className="flex-1 flex overflow-hidden px-3 sm:px-5 pb-4 pt-3">
        {/* OUTER CARD */}
        <div className="flex-1 rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md shadow-[0_18px_45px_rgba(0,0,0,0.65)] overflow-hidden flex">
          {/* SIDEBAR – FILES */}
          <aside className="w-60 sm:w-64 border-r border-white/10 bg-gradient-to-b from-slate-950/80 to-slate-950/40 p-3 sm:p-4 text-sm flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-white/80 text-xs uppercase tracking-[0.16em]">
                Workspace
              </span>
              <button
                onClick={createFile}
                className="flex items-center gap-1 text-[11px] text-sky-300 hover:text-sky-100"
              >
                <Plus size={13} /> New
              </button>
            </div>

            <div className="flex-1 overflow-auto space-y-3 pr-1">
              {folders.map((folder) => (
                <div key={folder}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="uppercase text-[10px] text-white/40 tracking-[0.16em]">
                      {folder === "root" ? "ROOT" : folder}
                    </div>
                    {folder === "templates" && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-200 border border-amber-400/40">
                        read-only
                      </span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {Object.keys(files)
                      .filter((f) => getFolderName(f) === folder)
                      .map((path) => (
                        <button
                          key={path}
                          type="button"
                          onClick={() => setActiveFile(path)}
                          className={`w-full text-left rounded-lg px-2.5 py-1.5 text-xs flex items-center justify-between ${
                            activeFile === path
                              ? "bg-sky-500/20 text-sky-100 border border-sky-500/50"
                              : "text-slate-200/80 hover:bg-white/5"
                          }`}
                        >
                          <span className="truncate">
                            {folder === "root"
                              ? path
                              : path.replace(`${folder}/`, "")}
                          </span>
                          {path.startsWith("tests/") && (
                            <span className="ml-1 text-[9px] px-1 py-0.5 rounded-full bg-emerald-400/10 text-emerald-200 border border-emerald-400/40">
                              test
                            </span>
                          )}
                        </button>
                      ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t border-white/5 space-y-2 text-[11px]">
              <button
                onClick={deleteFile}
                className="flex items-center gap-1.5 text-red-400/90 hover:text-red-200"
              >
                <Trash2 size={13} /> Delete current
              </button>
              <button
                onClick={downloadFile}
                className="flex items-center gap-1.5 text-sky-300/90 hover:text-sky-100"
              >
                <Download size={13} /> Download current
              </button>
              <p className="mt-2 text-[10px] text-white/40 leading-snug">
                Code runs in an in-browser sandbox (Pyodide). No access to your
                local file system or external network.
              </p>
              <p className="text-[10px] text-amber-200/80">
                templates/ folder is read-only — use it as assignment reference.
              </p>
            </div>
          </aside>

          {/* MAIN: EDITOR + OUTPUT + REPL */}
          <main className="flex-1 grid grid-cols-[minmax(0,2.1fr)_minmax(0,1.3fr)]">
            {/* EDITOR PANEL */}
            <section className="border-r border-white/10 flex flex-col bg-gradient-to-br from-slate-950/80 via-slate-950/60 to-slate-900/60">
              {/* Editor header */}
              <div className="h-11 px-3 sm:px-4 border-b border-white/10 flex items-center justify-between text-xs bg-white/5/50">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="h-2 w-2 rounded-full bg-red-500/80" />
                    <span className="h-2 w-2 rounded-full bg-amber-400/80" />
                    <span className="h-2 w-2 rounded-full bg-emerald-400/80" />
                  </div>
                  <span className="ml-2 font-mono text-[11px] text-slate-100 truncate max-w-[150px] sm:max-w-[260px]">
                    {activeFile}
                  </span>
                  {isTemplate && (
                    <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full border border-amber-400/60 bg-amber-400/10 text-amber-100">
                      read-only
                    </span>
                  )}
                </div>
                <span className="hidden sm:inline text-[10px] text-slate-300">
                  Python · Monaco Editor
                </span>
              </div>

              <div className="flex-1 min-h-0">
                <Editor
                  height="100%"
                  theme="vs-dark"
                  defaultLanguage="python"
                  value={files[activeFile]}
                  onChange={(value) => {
                    if (!isTemplate) {
                      setFiles((prev) => ({
                        ...prev,
                        [activeFile]: value ?? "",
                      }));
                    }
                  }}
                  onMount={handleEditorMount}
                  options={{
                    readOnly: isTemplate,
                    fontSize: 15,
                    minimap: { enabled: false },
                    smoothScrolling: true,
                    scrollBeyondLastLine: false,
                    wordWrap: "on",
                    tabSize: 4,
                    insertSpaces: true,
                  }}
                />
              </div>
            </section>

            {/* OUTPUT + REPL PANEL */}
            <section className="flex flex-col bg-gradient-to-br from-slate-950/80 via-slate-950/50 to-slate-900/60">
              {/* OUTPUT */}
              <div className="flex-1 border-b border-white/10 p-3 sm:p-4 overflow-auto text-xs sm:text-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-sky-300">
                      OUTPUT
                    </span>
                    <span className="text-[10px] text-slate-300/80">
                      (stdout / stderr from {activeFile})
                    </span>
                  </div>
                  <span className="text-[10px] text-white/40">
                    Env: Pyodide + Matplotlib
                  </span>
                </div>
                <div className="rounded-lg bg-black/50 border border-white/10 p-3 min-h-[110px]">
                  <pre className="whitespace-pre-wrap text-white/90 font-mono text-[11px] leading-relaxed">
                    {output}
                  </pre>

                  {plots.length > 0 && (
                    <div className="mt-4">
                      <div className="text-[11px] font-semibold text-sky-300 mb-2">
                        Matplotlib plots
                      </div>
                      <div className="space-y-3">
                        {plots.map((img, i) => (
                          <img
                            key={i}
                            src={`data:image/png;base64,${img}`}
                            alt={`Plot ${i + 1}`}
                            className="border border-white/10 rounded-lg max-h-64 bg-white"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* REPL */}
              <div className="h-48 p-3 sm:p-4 text-xs border-t border-white/5 bg-black/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Terminal size={14} className="text-sky-300" />
                    <span className="font-semibold text-sky-200 text-[11px]">
                      Python REPL
                    </span>
                    <span className="hidden sm:inline text-[10px] text-slate-300">
                      Shared with editor runtime
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-400">
                    Tip: <code className="font-mono">pip install numpy</code>
                  </span>
                </div>
                <div className="bg-black/60 border border-white/10 rounded-lg p-2 h-28 overflow-auto mb-2">
                  {replHistory.length === 0 && (
                    <div className="text-[10px] text-white/40">
                      Type Python code below and press <b>Run</b>.  
                      Use the same variables and imports as in your files.
                    </div>
                  )}
                  {replHistory.map((line, idx) => (
                    <pre
                      key={idx}
                      className="whitespace-pre-wrap text-white/85 mb-1 font-mono text-[11px]"
                    >
                      {line}
                    </pre>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    value={replInput}
                    onChange={(e) => setReplInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        runRepl();
                      }
                    }}
                    className="flex-1 rounded-lg bg-black/70 border border-white/15 px-2 py-1 text-white text-[11px] outline-none focus:ring-1 focus:ring-sky-500 font-mono"
                    placeholder='e.g. print(2 + 2) or pip install numpy'
                  />
                  <button
                    onClick={runRepl}
                    className="px-3 py-1 rounded-lg bg-sky-500 hover:bg-sky-600 text-[11px] font-semibold flex items-center gap-1 shadow-sm"
                  >
                    Run
                  </button>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
